import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Plus, Trash2, Save } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CreateInvoice = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_email: '',
    customer_address: '',
    customer_tin: '',
    due_date: '',
    notes: '',
    currency: 'MYR'
  });
  const [lineItems, setLineItems] = useState([
    { description: '', quantity: 1, unit_price: 0, tax_rate: 6, amount: 0 }
  ]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleLineItemChange = (index, field, value) => {
    const updated = [...lineItems];
    updated[index][field] = field === 'description' ? value : parseFloat(value) || 0;
    updated[index].amount = updated[index].quantity * updated[index].unit_price * (1 + updated[index].tax_rate / 100);
    setLineItems(updated);
  };

  const addLineItem = () => {
    setLineItems([...lineItems, { description: '', quantity: 1, unit_price: 0, tax_rate: 6, amount: 0 }]);
  };

  const removeLineItem = (index) => {
    if (lineItems.length > 1) {
      setLineItems(lineItems.filter((_, i) => i !== index));
    }
  };

  const calculateTotals = () => {
    const subtotal = lineItems.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
    const taxTotal = lineItems.reduce((sum, item) => sum + (item.quantity * item.unit_price * item.tax_rate / 100), 0);
    const total = subtotal + taxTotal;
    return { subtotal, taxTotal, total };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        ...formData,
        line_items: lineItems.map(item => ({
          description: item.description,
          quantity: item.quantity,
          unit_price: item.unit_price,
          tax_rate: item.tax_rate,
          amount: item.amount
        }))
      };

      await axios.post(`${API}/invoices`, payload);
      toast.success('Invoice created successfully!');
      navigate('/suite/finance/invoices');
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to create invoice';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-MY', {
      style: 'currency',
      currency: 'MYR'
    }).format(value || 0);
  };

  const totals = calculateTotals();

  return (
    <div className="space-y-6" data-testid="create-invoice-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button 
          variant="ghost" 
          size="icon"
          onClick={() => navigate('/suite/finance/invoices')}
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="font-heading font-bold text-2xl text-white">Create Invoice</h1>
          <p className="text-muted-foreground">Generate LHDN-compliant e-invoice</p>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="grid lg:grid-cols-12 gap-6">
          {/* Customer Details */}
          <motion.div 
            className="lg:col-span-8 space-y-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Card className="bg-orbit-card border-orbit-border">
              <CardHeader>
                <CardTitle className="text-white">Customer Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="customer_name">Customer Name *</Label>
                    <Input
                      id="customer_name"
                      name="customer_name"
                      value={formData.customer_name}
                      onChange={handleChange}
                      placeholder="Acme Sdn Bhd"
                      required
                      data-testid="customer-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="customer_email">Email *</Label>
                    <Input
                      id="customer_email"
                      name="customer_email"
                      type="email"
                      value={formData.customer_email}
                      onChange={handleChange}
                      placeholder="billing@acme.com"
                      required
                      data-testid="customer-email-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="customer_address">Address *</Label>
                  <Textarea
                    id="customer_address"
                    name="customer_address"
                    value={formData.customer_address}
                    onChange={handleChange}
                    placeholder="123 Jalan Ampang, 50450 Kuala Lumpur, Malaysia"
                    required
                    data-testid="customer-address-input"
                  />
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="customer_tin">TIN (Tax ID Number)</Label>
                    <Input
                      id="customer_tin"
                      name="customer_tin"
                      value={formData.customer_tin}
                      onChange={handleChange}
                      placeholder="C12345678901"
                      data-testid="customer-tin-input"
                    />
                    <p className="text-xs text-muted-foreground">Required for LHDN e-Invoice</p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="due_date">Due Date *</Label>
                    <Input
                      id="due_date"
                      name="due_date"
                      type="date"
                      value={formData.due_date}
                      onChange={handleChange}
                      required
                      data-testid="due-date-input"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Line Items */}
            <Card className="bg-orbit-card border-orbit-border">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-white">Line Items</CardTitle>
                <Button 
                  type="button"
                  variant="outline" 
                  size="sm"
                  onClick={addLineItem}
                  className="border-orbit-border"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Item
                </Button>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {lineItems.map((item, index) => (
                    <motion.div 
                      key={index}
                      className="grid grid-cols-12 gap-3 items-end p-4 rounded-lg bg-orbit-bg/50"
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                    >
                      <div className="col-span-12 sm:col-span-4 space-y-2">
                        <Label>Description</Label>
                        <Input
                          value={item.description}
                          onChange={(e) => handleLineItemChange(index, 'description', e.target.value)}
                          placeholder="Service/Product description"
                          data-testid={`line-item-desc-${index}`}
                        />
                      </div>
                      <div className="col-span-4 sm:col-span-2 space-y-2">
                        <Label>Qty</Label>
                        <Input
                          type="number"
                          min="1"
                          value={item.quantity}
                          onChange={(e) => handleLineItemChange(index, 'quantity', e.target.value)}
                          data-testid={`line-item-qty-${index}`}
                        />
                      </div>
                      <div className="col-span-4 sm:col-span-2 space-y-2">
                        <Label>Price (MYR)</Label>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={item.unit_price}
                          onChange={(e) => handleLineItemChange(index, 'unit_price', e.target.value)}
                          data-testid={`line-item-price-${index}`}
                        />
                      </div>
                      <div className="col-span-4 sm:col-span-2 space-y-2">
                        <Label>SST %</Label>
                        <Input
                          type="number"
                          min="0"
                          max="100"
                          value={item.tax_rate}
                          onChange={(e) => handleLineItemChange(index, 'tax_rate', e.target.value)}
                          data-testid={`line-item-tax-${index}`}
                        />
                      </div>
                      <div className="col-span-11 sm:col-span-1 text-right">
                        <p className="text-sm text-muted-foreground mb-1">Amount</p>
                        <p className="font-mono text-white">{formatCurrency(item.amount)}</p>
                      </div>
                      <div className="col-span-1">
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => removeLineItem(index)}
                          disabled={lineItems.length === 1}
                          className="text-destructive hover:text-destructive"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Notes */}
            <Card className="bg-orbit-card border-orbit-border">
              <CardHeader>
                <CardTitle className="text-white">Additional Notes</CardTitle>
              </CardHeader>
              <CardContent>
                <Textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  placeholder="Payment terms, bank details, or other notes..."
                  rows={4}
                  data-testid="invoice-notes"
                />
              </CardContent>
            </Card>
          </motion.div>

          {/* Summary */}
          <motion.div 
            className="lg:col-span-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <Card className="bg-orbit-card border-orbit-border sticky top-24">
              <CardHeader>
                <CardTitle className="text-white">Invoice Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Subtotal</span>
                    <span className="text-white font-mono">{formatCurrency(totals.subtotal)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">SST</span>
                    <span className="text-white font-mono">{formatCurrency(totals.taxTotal)}</span>
                  </div>
                  <div className="border-t border-orbit-border pt-3">
                    <div className="flex justify-between">
                      <span className="font-semibold text-white">Total</span>
                      <span className="text-xl font-heading font-bold text-white tabular-nums">
                        {formatCurrency(totals.total)}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="pt-4 space-y-3">
                  <Button 
                    type="submit"
                    className="w-full bg-orbit-glow hover:opacity-90"
                    disabled={loading}
                    data-testid="create-invoice-submit"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    {loading ? 'Creating...' : 'Create Invoice'}
                  </Button>
                  <Button 
                    type="button"
                    variant="outline"
                    className="w-full border-orbit-border"
                    onClick={() => navigate('/suite/finance/invoices')}
                  >
                    Cancel
                  </Button>
                </div>

                <div className="pt-4 p-4 rounded-lg bg-primary/10 border border-primary/20">
                  <p className="text-xs text-primary">
                    This invoice will be LHDN MyInvois compliant. You can submit it to LHDN after creation.
                  </p>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </form>
    </div>
  );
};

export default CreateInvoice;
